/**
 * Loan arithmetic. Pure.
 *
 * `monthlyPayment` is deliberately NOT reimplemented here — it is imported from
 * src/core/rates.ts, where it is already pinned against standard amortization
 * tables in test/core.test.ts. The only new arithmetic in this file is its exact
 * inverse and the schedule builder.
 */

import { monthlyPayment, totalInterest, round2 } from '../../src/core/rates.js';
import type { Amortization, ScheduleRow } from './types.js';

export { monthlyPayment, totalInterest, round2 };

/**
 * The principal a given payment supports: the exact inverse of
 * `monthlyPayment`. From P = L·r·f/(f−1) with f = (1+r)^n, so L = P·(f−1)/(r·f).
 *
 * Used to answer "what can I borrow at this payment", and to invert a coverage
 * floor into a loan amount.
 */
export function principalForPayment(
  payment: number,
  annualRatePct: number,
  termMonths: number,
): number {
  if (termMonths <= 0) throw new Error('termMonths must be positive');
  if (payment <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return payment * termMonths;
  const f = Math.pow(1 + r, termMonths);
  return (payment * (f - 1)) / (r * f);
}

/**
 * A full schedule, with an optional interest-only period at the front.
 *
 * Many real 7(a) deals — construction, commercial real estate, practice
 * acquisitions — carry 6 to 24 months of interest-only before principal starts.
 * Showing a fully-amortizing payment from month one is simply the wrong payment
 * for those borrowers, and the payment that follows amortizes the balance over
 * the REMAINING term, not the original one.
 *
 * The final row absorbs any residual cent, so the closing balance is exactly
 * zero and the totals reconcile against the principal.
 */
export function amortize(
  principal: number,
  annualRatePct: number,
  termMonths: number,
  interestOnlyMonths = 0,
): Amortization {
  if (termMonths <= 0) throw new Error('termMonths must be positive');
  if (interestOnlyMonths < 0) throw new Error('interestOnlyMonths cannot be negative');
  if (interestOnlyMonths >= termMonths) {
    throw new Error('interestOnlyMonths must be shorter than the term');
  }

  const r = annualRatePct / 100 / 12;
  const amortizingMonths = termMonths - interestOnlyMonths;
  // Round the payment to cents once, then derive principal from it, so every
  // row satisfies payment = interest + principal exactly and the totals
  // reconcile against the principal rather than drifting over a long term.
  const payment = round2(monthlyPayment(principal, annualRatePct, amortizingMonths));
  const ioPayment = interestOnlyMonths > 0 ? round2(principal * r) : null;

  const rows: ScheduleRow[] = [];
  let balance = principal;

  for (let month = 1; month <= termMonths; month++) {
    const interest = round2(balance * r);
    const isIo = month <= interestOnlyMonths;
    const isLast = month === termMonths;

    let principalPart: number;
    let paid: number;
    if (isIo) {
      principalPart = 0;
      paid = interest;
    } else if (isLast) {
      // Absorb the residual so the loan closes at exactly zero.
      principalPart = round2(balance);
      paid = round2(principalPart + interest);
    } else {
      principalPart = round2(payment - interest);
      paid = payment;
    }

    balance = round2(balance - principalPart);
    rows.push({
      month,
      payment: paid,
      interest,
      principal: principalPart,
      balance,
      interestOnly: isIo,
    });
  }

  const totalPaid = round2(rows.reduce((sum, row) => sum + row.payment, 0));
  const interestSum = round2(rows.reduce((sum, row) => sum + row.interest, 0));

  return {
    rows,
    interestOnlyPayment: ioPayment,
    payment,
    totalInterest: interestSum,
    totalPaid,
  };
}

/**
 * The balance still owed after `months` payments — what a borrower owes at a
 * balloon or call date, and the base for a prepayment calculation.
 */
export function balanceAfter(
  principal: number,
  annualRatePct: number,
  termMonths: number,
  months: number,
  interestOnlyMonths = 0,
): number {
  if (months <= 0) return round2(principal);
  if (months >= termMonths) return 0;
  const { rows } = amortize(principal, annualRatePct, termMonths, interestOnlyMonths);
  return rows[months - 1]?.balance ?? 0;
}
